import {
    activateTwoStepAuthentication,
    deactivateTwoStepAuthentication,
} from '@/redux/modules/account/twoStepAuthentication/twoStepAuthentication.actions';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getTwoStepAuthenticationActivated } from '@/redux/modules/account/twoStepAuthentication/twoStepAuthentication.selectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import TwoStepAuthenticationActivationModal from './components/ActivationModal/TwoStepAuthenticationActivationModal';
import TwoStepAuthenticationDeactivationModal from './components/DeactivationModal/TwoStepAuthenticationDeactivationModal';

const TwoStepAuthentication = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const bidderId = useAppSelector(getBidderId);
    const hasTwoStepActive = useAppSelector(getTwoStepAuthenticationActivated);
    const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);

    const openModal = () => {
        if (hasTwoStepActive) {
            setIsDeactivateModalOpen(true);
        } else {
            setIsActivateModalOpen(true);
        }
    };

    const onCloseModal = () => {
        setIsActivateModalOpen(false);
        setIsDeactivateModalOpen(false);
    };

    return (
        <article>
            <TwoStepAuthenticationActivationModal
                onActivate={() => {
                    dispatch(activateTwoStepAuthentication({ bidderId }));
                    onCloseModal();
                }}
                onCancel={onCloseModal}
                show={isActivateModalOpen}
            />
            <TwoStepAuthenticationDeactivationModal
                onCancel={onCloseModal}
                onDeactivate={() => {
                    dispatch(deactivateTwoStepAuthentication(bidderId));
                    onCloseModal();
                }}
                show={isDeactivateModalOpen}
            />
            <StyledSubsectionHeaderRow>
                <StyledHeader>
                    <FormattedMessage id="accountSettings.security.twoStepAuthentication.header" />
                </StyledHeader>
                <StyledActiveStatusText $isActive={hasTwoStepActive}>
                    <FormattedMessage
                        id={`accountSettings.security.twoStepAuthentication.${
                            hasTwoStepActive ? 'active' : 'inactive'
                        }`}
                    />
                </StyledActiveStatusText>
            </StyledSubsectionHeaderRow>
            <StyledSubsectionDescription>
                <StyledSubsectionDescriptionText color="secondary">
                    <FormattedMessage id="accountSettings.security.twoStepAuthentication.description" />
                </StyledSubsectionDescriptionText>
            </StyledSubsectionDescription>
            <StyledSubsectionButton
                onClick={openModal}
                variant={hasTwoStepActive ? 'outlined' : 'filled'}
            >
                {formatMessage({
                    id: `accountSettings.security.twoStepAuthentication.${
                        hasTwoStepActive ? 'deactivate' : 'activate'
                    }`,
                })}
            </StyledSubsectionButton>
        </article>
    );
};

export default TwoStepAuthentication;

const StyledSubsectionHeaderRow = styled.header`
    margin-bottom: 8px;
`;

const StyledHeader = styled(BodyPrimary)`
    ${({
        theme: {
            breakpoints: { tabletNarrowWidth },
        },
    }) => css`
        @media (max-width: ${tabletNarrowWidth}) {
            // we always want to have consistent appearances between desktop and mobile. So here we are.
            font-size: 16px;
            line-height: 24px;
        }
    `}
`;

type ActiveStatusStyleOptions = {
    $isActive: boolean;
};
const StyledActiveStatusText = styled(BodySecondary)<ActiveStatusStyleOptions>`
    ${({
        $isActive,
        theme: {
            breakpoints: { tabletNarrowWidth },
            colors: { grey300, teal },
        },
    }) => css`
        margin-left: 8px;
        color: ${$isActive ? teal : grey300};

        @media (max-width: ${tabletNarrowWidth}) {
            // we always want to have consistent appearances between desktop and mobile. So here we are.
            font-size: 14px;
            line-height: 21px;
        }
    `}
`;

const StyledSubsectionDescription = styled.p`
    margin-bottom: 32px;
`;

const StyledSubsectionDescriptionText = styled(BodySecondary)`
    ${({
        theme: {
            breakpoints: { tabletNarrowWidth },
        },
    }) => css`
        @media (max-width: ${tabletNarrowWidth}) {
            // we always want to have consistent appearances between desktop and mobile. So here we are.
            font-size: 14px;
            line-height: 21px;
        }
    `}
`;

const StyledSubsectionButton = styled(Button)`
    width: 194px;
    @media (max-width: 500px) {
        width: 100%;
    }
`;
